import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Search, X } from 'lucide-react'
import { searchNavigationTarget } from '../../utils/searchNavigation'

const ICON_BTN =
  'inline-flex items-center justify-center p-1.5 text-ink-500 hover:text-ink transition-colors rounded-sm'

/**
 * Collapsible header search — magnifier expands to an inline field.
 */
const HeaderSearch: React.FC = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const target = searchNavigationTarget(query)
    if (!target) return
    navigate(target)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={wrapRef} className="flex items-center">
      <form
        onSubmit={handleSubmit}
        className={`flex items-center overflow-hidden transition-[width] duration-200 ease-out ${
          open ? 'w-44 sm:w-56' : 'w-9'
        }`}
      >
        {open ? (
          <>
            <label htmlFor="header-search" className="sr-only">
              CVR-nummer eller virksomhedsnavn
            </label>
            <input
              ref={inputRef}
              id="header-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="CVR eller navn"
              className="min-w-0 flex-1 bg-transparent border-b border-rule py-1 text-sm text-ink placeholder:text-ink-400 focus:outline-none focus:border-ink"
            />
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setQuery('')
              }}
              className={ICON_BTN}
              aria-label="Luk søg"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={ICON_BTN}
            aria-label="Søg"
          >
            <Search className="h-4 w-4" aria-hidden />
          </button>
        )}
      </form>
    </div>
  )
}

export default HeaderSearch
